import type React from 'react'
import { useMemo, useState } from 'react'
import { memo } from 'react'

import {
  Button,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  IconDatabase,
  IconExclamationCircle,
  IconSearch,
  IconSnowflake,
  IconUsers,
} from '@tabler/icons-react'
import type Decimal from 'decimal.js-light'

import { TowerCard } from '@/components/ui/TowerCard'
import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'
import type { FundHolder, FundRegistryProps } from '@/hooks/useFundRegistry'
import { useFundRegistry } from '@/hooks/useFundRegistry'

const FundRegistry = memo<FundRegistryProps>(function FundRegistry({
  fundId,
  effective_at,
}: FundRegistryProps) {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [debouncedSearchQuery] = useDebouncedValue(searchInputValue, 300)

  const {
    stats,
    holders,
    pagination,
    isLoading,
    error,
    loadMore,
    updateSearch,
  } = useFundRegistry({ fundId, effective_at })

  // Update search when debounced value changes
  useMemo(() => {
    updateSearch(debouncedSearchQuery)
  }, [debouncedSearchQuery, updateSearch])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.currentTarget.value)
  }

  const handleRowClick = (holder: FundHolder) => {
    if (holder.profileUrl) {
      window.open(holder.profileUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatShares = (shares: number | Decimal) => {
    const numValue = typeof shares === 'number' ? shares : shares.toNumber()
    return numValue.toLocaleString('en-US')
  }

  const formatValue = (value?: number | Decimal) => {
    if (value === undefined) return ''
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const columns: TowerDataTableColumn<FundHolder>[] = [
    mapColumn({
      title: 'Name/Wallet Address',
      width: '25%',
      render: (_, holder: FundHolder) => (
        <Stack gap={2}>
          {!holder.isVault && (
            <Text fw={500} size="sm">
              {holder.name || holder.walletAddress}
            </Text>
          )}
          {holder.name && (
            <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
              {holder.walletAddress}
            </Text>
          )}
        </Stack>
      ),
    }),
    column({
      accessor: 'totalShares',
      title: 'Shares',
      width: '20%',
      textAlign: 'right',
      render: (shares) => (
        <Text fw={500} size="sm">
          {formatShares(shares)}
        </Text>
      ),
    }),
    column({
      accessor: 'totalValue',
      title: (
        <Stack gap={0}>
          <Text size="sm" fw={600}>
            Current Value
          </Text>
          <Text size="xs" c="dimmed">
            @ {formatValue(stats?.currentPrice)} / token
          </Text>
        </Stack>
      ),
      width: '20%',
      textAlign: 'right',
      render: (value: Decimal) => (
        <Text fw={500} size="sm" c="green.6">
          {formatValue(value.toNumber())}
        </Text>
      ),
    }),
    column({
      accessor: 'totalTransactions',
      title: 'Transactions',
      width: '15%',
      textAlign: 'right',
      render: (transactions) => (
        <Text size="sm">{transactions.toLocaleString('en-US')}</Text>
      ),
    }),
    mapColumn({
      title: 'Active',
      width: '20%',
      render: (_, holder) =>
        holder.firstTransactionAt != holder.lastTransactionAt ? (
          <Text size="sm">
            {formatDate(holder.firstTransactionAt)}
            <br />
            thru {formatDate(holder.lastTransactionAt)}
          </Text>
        ) : (
          <Text size="sm">{formatDate(holder.firstTransactionAt)}</Text>
        ),
    }),
  ]

  const headerContent = (
    <Group>
      <IconSearch size={16} />
      <TextInput
        placeholder="Search holders..."
        value={searchInputValue}
        onChange={handleSearchChange}
        size="sm"
        style={{ width: 250 }}
      />
    </Group>
  )

  // const registryViewIndicator = effective_at ? (
  //   <Group
  //     gap="xs"
  //     mb="md"
  //     p="xs"
  //     style={{
  //       backgroundColor: 'var(--mantine-color-blue-0)',
  //       borderRadius: 'var(--mantine-radius-sm)',
  //       border: '1px solid var(--mantine-color-blue-2)',
  //     }}
  //   >
  //     <IconClock size={16} color="var(--mantine-color-blue-6)" />
  //     <Text size="sm" c="blue.7">
  //       Registry as of {formatDateTime(effective_at)}
  //     </Text>
  //   </Group>
  // ) : (
  //   <Group
  //     gap="xs"
  //     mb="md"
  //     p="xs"
  //     style={{
  //       backgroundColor: 'var(--mantine-color-green-0)',
  //       borderRadius: 'var(--mantine-radius-sm)',
  //       border: '1px solid var(--mantine-color-green-2)',
  //     }}
  //   >
  //     <IconDatabase size={16} color="var(--mantine-color-green-6)" />
  //     <Text size="sm" c="green.7">
  //       Current Registry
  //     </Text>
  //   </Group>
  // )

  const statsGrid = isLoading ? (
    <SimpleGrid cols={4} spacing="md" mb="md">
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} p="md" withBorder radius="md">
          <Group gap="xs" mb="xs">
            <Skeleton height={20} width={20} radius="sm" />
            <Skeleton height={16} width={80} />
          </Group>
          <Skeleton height={24} width={60} />
        </Paper>
      ))}
    </SimpleGrid>
  ) : (
    <SimpleGrid cols={4} spacing="md" mb="md">
      <Paper p="md" withBorder radius="md">
        <Group gap="xs" mb="xs">
          <IconDatabase size={20} color="var(--mantine-color-blue-6)" />
          <Text size="sm" c="dimmed">
            Total Tokens
          </Text>
        </Group>
        <Text fw={700} size="lg">
          {stats?.totalShares.toNumber().toLocaleString('en-US') || '0'}
        </Text>
      </Paper>

      <Paper p="md" withBorder radius="md">
        <Group gap="xs" mb="xs">
          <IconUsers size={20} color="var(--mantine-color-green-6)" />
          <Text size="sm" c="dimmed">
            Total Holders
          </Text>
        </Group>
        <Text fw={700} size="lg">
          {stats?.totalHolders.toLocaleString('en-US') || '0'}
        </Text>
      </Paper>

      <Paper p="md" withBorder radius="md">
        <Group gap="xs" mb="xs">
          <IconSnowflake size={20} color="var(--mantine-color-cyan-6)" />
          <Text size="sm" c="dimmed">
            Frozen Tokens
          </Text>
        </Group>
        <Text fw={700} size="lg">
          {/*? {stats?.totalFrozenTokens.toLocaleString('en-US') || '0'}*/}
        </Text>
      </Paper>

      <Paper p="md" withBorder radius="md">
        <Group gap="xs" mb="xs">
          <IconExclamationCircle
            size={20}
            color="var(--mantine-color-orange-6)"
          />
          <Text size="sm" c="dimmed">
            Exception Cases
          </Text>
        </Group>
        <Text fw={700} size="lg">
          {/*{stats?.totalExceptionCases.toLocaleString('en-US') || '0'}*/}
        </Text>
      </Paper>
    </SimpleGrid>
  )

  if (error) {
    return (
      <TowerCard
        title="Fund Registry"
        description="Token holders and distribution overview"
        icon={<IconDatabase size={20} />}
      >
        <Stack>
          <Text c="red">Error loading fund registry data: {error.message}</Text>
        </Stack>
      </TowerCard>
    )
  }

  return (
    <TowerCard
      title="Fund Registry"
      description="Token holders and distribution overview"
      icon={<IconDatabase size={20} />}
      extraHeader={headerContent}
    >
      <Stack>
        {statsGrid}

        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, index) => (
              <Paper key={index} p="md" withBorder radius="md">
                <Group justify="space-between">
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Skeleton height={16} width="60%" />
                    <Skeleton height={12} width="80%" />
                  </Stack>
                  <Group gap="xl">
                    <Skeleton height={16} width={80} />
                    <Skeleton height={16} width={100} />
                    <Skeleton height={16} width={60} />
                    <Skeleton height={16} width={90} />
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        ) : (
          <TowerDataTable
            data={holders}
            columns={columns}
            emptyMessage="No fund holders found"
            isLoading={isLoading}
            onRowClick={handleRowClick}
          />
        )}

        {pagination.hasMore && (
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={loadMore} loading={isLoading}>
              Show More
            </Button>
          </Group>
        )}
      </Stack>
    </TowerCard>
  )
})

export default FundRegistry
